// src/components/Projects/ProjectDashboard.jsx
import React, { useState, useEffect } from 'react';
import supabase from '../../supabase';
import ProjectForm from './ProjectForm';
import ProjectPage from './ProjectPage';
import './ProjectDashboard.css';
import { PlusCircle, ChevronRight, Trash2 } from 'lucide-react';

const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'form', 'view'

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please log in to view projects');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const projectsWithClients = await Promise.all(
        (data || []).map(async (project) => {
          if (project.client_id) {
            const { data: clientData } = await supabase
              .from('clients')
              .select('client_name')
              .eq('id', project.client_id)
              .single();
            return { ...project, client_name: clientData?.client_name || null };
          }
          return project;
        })
      );

      setProjects(projectsWithClients);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (e, projectId, projectName) => {
    e.stopPropagation(); // Prevent card click event
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the project "${projectName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      // Remove the deleted project from state
      setProjects(projects.filter(project => project.id !== projectId));
      
      // Show success feedback (optional)
      alert('Project deleted successfully');
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project: ' + err.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      planning: 'prjctdashb-status-planning',
      active: 'prjctdashb-status-active',
      on_hold: 'prjctdashb-status-hold',
      completed: 'prjctdashb-status-completed',
      cancelled: 'prjctdashb-status-cancelled'
    };
    return statusMap[status] || '';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleAddProject = () => {
    setSelectedProject(null);
    setViewMode('form');
    setShowProjectForm(true);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setViewMode('view');
  };

  const handleCloseForm = () => {
    setShowProjectForm(false);
    setSelectedProject(null);
    setViewMode('dashboard');
    fetchProjects();
  };

  const handleBackToDashboard = () => {
    setSelectedProject(null);
    setViewMode('dashboard');
    fetchProjects();
  };

  const generateFallbackAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Project')}&size=64&background=16a34a&color=ffffff&bold=true`;
  };

  if (viewMode === 'view' && selectedProject) {
    return <ProjectPage project={selectedProject} onBack={handleBackToDashboard} />;
  }

  if (viewMode === 'form' && showProjectForm) {
    return <ProjectForm project={selectedProject} onClose={handleCloseForm} />;
  }

  return (
    <div className="prjctdashb-container">
      <div className="prjctdashb-header">
        <div className="prjctdashb-header-content">
          <h1 className="prjctdashb-title">Projects</h1>
        </div>
        <button className="prjctdashb-add-btn" onClick={handleAddProject}>
          <span>Add Project</span>
          <PlusCircle size={18} className="add-icon" />
        </button>
      </div>

      {loading && (
        <div className="prjctdashb-loading">
          <div className="prjctdashb-spinner"></div>
          <p>Loading projects...</p>
        </div>
      )}

      {error && (
        <div className="prjctdashb-error">
          <p>Error: {error}</p>
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="prjctdashb-empty">
          <div className="prjctdashb-empty-icon">📋</div>
          <h2>No projects yet</h2>
          <p>Get started by creating your first project</p>
          <button className="prjctdashb-empty-btn" onClick={handleAddProject}>
            Create Project
          </button>
        </div>
      )}

      {!loading && !error && projects.length > 0 && (
        <div className="prjctdashb-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className="prjctdashb-card"
              onClick={() => handleViewProject(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleViewProject(project);
              }}
              aria-label={`View ${project.name} project details`}
            >
              <div className="prjctdashb-card-content">
                <div className="prjctdashb-card-header">
                  <img
                    src={generateFallbackAvatar(project.name)}
                    alt={`${project.name} icon`}
                    className="prjctdashb-card-avatar"
                    onError={(e) => { e.currentTarget.src = generateFallbackAvatar(project.name); }}
                    loading="lazy"
                  />
                  <div className="prjctdashb-card-info">
                    <h3 className="prjctdashb-card-name" title={project.name}>
                      {project.name}
                    </h3>
                    <p className={`prjctdashb-status-badge ${getStatusBadgeClass(project.status)}`}>
                      {(project.status || 'unknown').replace('_', ' ')}
                    </p>
                  </div>
                </div>

                {project.description && (
                  <p className="prjctdashb-card-description">{project.description}</p>
                )}

                <div className="prjctdashb-card-details">
                  {project.client_name && (
                    <div className="prjctdashb-detail-item">
                      <span className="prjctdashb-detail-label">Client:</span>
                      <span className="prjctdashb-detail-value">{project.client_name}</span>
                    </div>
                  )}

                  {project.project_type && (
                    <div className="prjctdashb-detail-item">
                      <span className="prjctdashb-detail-label">Type:</span>
                      <span className="prjctdashb-detail-value">{project.project_type}</span>
                    </div>
                  )}

                  <div className="prjctdashb-detail-item">
                    <span className="prjctdashb-detail-label">Start Date:</span>
                    <span className="prjctdashb-detail-value">{formatDate(project.start_date)}</span>
                  </div>

                  <div className="prjctdashb-detail-item">
                    <span className="prjctdashb-detail-label">End Date:</span>
                    <span className="prjctdashb-detail-value">{formatDate(project.end_date)}</span>
                  </div>

                  {project.budget !== undefined && project.budget !== null && (
                    <div className="prjctdashb-detail-item">
                      <span className="prjctdashb-detail-label">Budget:</span>
                      <span className="prjctdashb-detail-value prjctdashb-budget">
                        {formatCurrency(project.budget)}
                      </span>
                    </div>
                  )}

                  {project.Tax_type && (
                    <div className="prjctdashb-detail-item">
                      <span className="prjctdashb-detail-label">Tax:</span>
                      <span className="prjctdashb-detail-value">
                        {project.Tax_type} ({project.Tax_value}%)
                      </span>
                    </div>
                  )}
                </div>

                <div className="prjctdashb-card-footer">
                  <span className="prjctdashb-card-view-details">
                    View Details
                    <ChevronRight size={16} />
                  </span>
                  <button
                    className="prjctdashb-delete-btn"
                    onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                    aria-label={`Delete ${project.name} project`}
                    title="Delete project"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard;